import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { CPGABI } from '../ABI/ABI';
import {
    ensureBackendSession,
    getCompanyTasks,
    getEmployeeTasks,
    getMyCompany,
    getMyEmployeeCompany,
    getNotifications,
    getMyDisputes,
    getMyPaymentDisputes,
    markNotificationRead,
} from '../api/backendClient';
import { CPGAddress } from '../constant/constant';
import { readContractData } from '../utils/contractInstance';
import { countMappingEntries } from '../utils/cpgDiamondReader';

const NotificationContext = createContext(null);

const POLL_MS = 60_000;

async function fetchTaskTotal(fetcher, token, statusFilter) {
    try {
        const result = await fetcher(token, {
            statusFilter,
            limit: 1,
            page: 1,
        });
        if (result?.pagination?.total != null) {
            return Number(result.pagination.total) || 0;
        }
        if (Array.isArray(result)) return result.length;
        if (Array.isArray(result?.items)) return result.items.length;
        return 0;
    } catch {
        return 0;
    }
}

export function NotificationProvider({ children }) {
    const { address: walletAddress, isConnected } = useAccount();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [counts, setCounts] = useState({
        unread: 0,
        ongoing: 0,
        disputes: 0,
        companyApprovals: 0,
        employeePending: 0,
        openJobDisputes: 0,
    });
    const [roles, setRoles] = useState({ isCompany: false, isEmployee: false });
    const refreshTimerRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!walletAddress || !isConnected) {
            setNotifications([]);
            setCounts({
                unread: 0,
                ongoing: 0,
                disputes: 0,
                companyApprovals: 0,
                employeePending: 0,
                openJobDisputes: 0,
            });
            setRoles({ isCompany: false, isEmployee: false });
            return;
        }

        setLoading(true);
        try {
            const token = await ensureBackendSession(walletAddress);
            if (!token) return;

            const nextCounts = {
                unread: 0,
                ongoing: 0,
                disputes: 0,
                companyApprovals: 0,
                employeePending: 0,
                openJobDisputes: 0,
            };

            const [
                notificationRows,
                company,
                employeeCompany,
                ongoingResult,
                disputeResult,
                paymentDisputesResult,
            ] = await Promise.allSettled([
                getNotifications(token),
                getMyCompany(token),
                getMyEmployeeCompany(token),
                countMappingEntries(
                    readContractData,
                    CPGAddress,
                    CPGABI,
                    'lockedPaymentsByReceiver',
                    walletAddress,
                ),
                countMappingEntries(
                    readContractData,
                    CPGAddress,
                    CPGABI,
                    'disputesByAccount',
                    walletAddress,
                ),
                getMyDisputes(token),
            ]);

            const rows =
                notificationRows.status === 'fulfilled'
                    ? notificationRows.value || []
                    : [];
            setNotifications(rows);
            nextCounts.unread = rows.filter((n) => !n.readStatus).length;

            const isCompany =
                company.status === 'fulfilled' && Boolean(company.value);
            const isEmployee =
                employeeCompany.status === 'fulfilled' &&
                Boolean(employeeCompany.value);
            setRoles({ isCompany, isEmployee });

            if (ongoingResult.status === 'fulfilled') {
                nextCounts.ongoing = Number(ongoingResult.value) || 0;
            }
            if (disputeResult.status === 'fulfilled') {
                nextCounts.disputes = Number(disputeResult.value) || 0;
            }

            const taskFetches = [];
            if (isCompany) {
                taskFetches.push(
                    fetchTaskTotal(getCompanyTasks, token, 'submitted').then(
                        (n) => {
                            nextCounts.companyApprovals = n;
                        },
                    ),
                    fetchTaskTotal(getCompanyTasks, token, 'disputed').then(
                        (n) => {
                            nextCounts.openJobDisputes += n;
                        },
                    ),
                );
            }
            if (isEmployee) {
                taskFetches.push(
                    fetchTaskTotal(getEmployeeTasks, token, 'pending').then(
                        (n) => {
                            nextCounts.employeePending = n;
                        },
                    ),
                    fetchTaskTotal(getEmployeeTasks, token, 'submitted').then(
                        (n) => {
                            /* employee submitted = waiting on company */
                        },
                    ),
                );
            }

            await Promise.all(taskFetches);

            if (paymentDisputesResult.status === 'fulfilled') {
                const paymentDisputes = (paymentDisputesResult.value || []).filter(
                    (d) => String(d?.type || '') !== 'support',
                );
                const openPayment = paymentDisputes.filter(
                    (d) =>
                        String(d?.status || '').toLowerCase() !== 'resolved' &&
                        String(d?.status || '').toLowerCase() !== 'closed',
                ).length;
                if (openPayment > nextCounts.disputes) {
                    nextCounts.disputes = openPayment;
                }
            }

            setCounts(nextCounts);
        } finally {
            setLoading(false);
        }
    }, [walletAddress, isConnected]);

    useEffect(() => {
        refresh();
    }, [refresh, location.pathname]);

    useEffect(() => {
        if (!walletAddress || !isConnected) return undefined;
        refreshTimerRef.current = setInterval(refresh, POLL_MS);
        return () => {
            if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        };
    }, [walletAddress, isConnected, refresh]);

    const markRead = useCallback(
        async (notificationId) => {
            if (!walletAddress || !notificationId) return;
            const token = await ensureBackendSession(walletAddress);
            if (!token) return;
            try {
                await markNotificationRead(token, notificationId);
                setNotifications((prev) =>
                    prev.map((n) =>
                        n.id === notificationId ? { ...n, readStatus: true } : n,
                    ),
                );
                setCounts((prev) => ({
                    ...prev,
                    unread: Math.max(0, prev.unread - 1),
                }));
            } catch {
                // ignore
            }
        },
        [walletAddress],
    );

    const markAllRead = useCallback(async () => {
        const unread = notifications.filter((n) => !n.readStatus);
        if (!unread.length) return;
        await Promise.all(unread.map((n) => markRead(n.id)));
    }, [notifications, markRead]);

    const menuBadges = useMemo(() => {
        const tasks =
            (roles.isCompany ? counts.companyApprovals : 0) +
            (roles.isEmployee ? counts.employeePending : 0);

        return {
            ongoing: counts.ongoing,
            disputes: counts.disputes,
            tasks,
            companyJob: tasks + counts.openJobDisputes,
            companyApprovals: counts.companyApprovals,
            employeePending: counts.employeePending,
            workspace: counts.openJobDisputes,
        };
    }, [counts, roles]);

    const value = useMemo(
        () => ({
            loading,
            notifications,
            counts,
            menuBadges,
            roles,
            unreadCount: counts.unread,
            refresh,
            markRead,
            markAllRead,
        }),
        [
            loading,
            notifications,
            counts,
            menuBadges,
            roles,
            refresh,
            markRead,
            markAllRead,
        ],
    );

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    const ctx = useContext(NotificationContext);
    if (!ctx) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return ctx;
}

export function useNotificationsOptional() {
    return useContext(NotificationContext);
}
